import { FieldProvenanceDto, SimilarTechnologySummaryDto, TechnologyTrlAnalysisChatKind } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { ConfidenceScore } from '@components/shared/confidence-score.component';
import { FieldProvenanceBadge } from '@components/shared/field-provenance-badge.component';
import { LlmChatModal } from '@components/shared/llm-chat/llm-chat-modal.component';
import { Tile } from '@components/shared/tile.component';
import { Button } from '@components/ui/button';
import { errorToast, successToast } from '@hooks/toast.hook';
import { useGetTechnology, useSaveSimilarTechnologies } from '@queries/technology.query';
import { useGetCurrentUser } from '@queries/users.query';
import { isChildOrganizationView as getIsChildOrganizationView } from '@utils/parent-organization.util';
import { Loader2Icon, SparklesIcon } from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import { SIMILAR_TECHNOLOGIES_PROMPT_GUIDELINES } from '../../../../../../constants/prompts.constants';
import { getTechnologyTrlAnalysisChatEndpoint } from '../../../../../../constants/technology-endpoints.constants';
import { SimilarTechnologiesSuggestionContent } from './similar-technologies-suggestion.component';
import {
  SimilarTechnologiesSummaryContent,
  getOverallSimilarTechnologiesConfidence,
} from './similar-technologies-summary-content.component';
import { StaleAssessmentTooltip } from './stale-assessment-tooltip.component';
import { parseSimilarTechnologiesSummaryData } from './trl-analysis-suggestion.util';

type SimilarTechnologiesTileProps = {
  assessmentId: string | null;
  isLatestAssessment: boolean;
  isLoading?: boolean;
  provenance?: FieldProvenanceDto | null;
  similarTechnologies: SimilarTechnologySummaryDto | null;
  technologyId: string;
};

const getCreateDescription = (technologyName?: string) => (
  <>
    Use Tyr&apos;s AI assistant to find similar technologies for <b>{technologyName || 'this technology'}</b>. All
    relevant metadata is used automatically — generate a draft, then refine it in conversation until it is ready to
    accept.
  </>
);

const getUpdateDescription = (technologyName?: string) => (
  <>
    The current similar technologies for <b>{technologyName || 'this technology'}</b> are shown on the right. Describe
    the changes you want, select text in them to suggest edits, or regenerate the list from scratch.
  </>
);

export const SimilarTechnologiesTile: FC<SimilarTechnologiesTileProps> = (props) => {
  const { assessmentId, isLatestAssessment, isLoading, provenance, similarTechnologies, technologyId } = props;
  const [isChatOpen, setIsChatOpen] = useState(false);

  const { isInnovator, isSelectedCompanyParentOrganization, selectedCompany } = useGetCurrentUser();
  const { data: technology, isPending: isTechnologyPending } = useGetTechnology(technologyId);
  // Only allow generation when we have an assessment to generate for
  const isChildOrganizationView = getIsChildOrganizationView({
    isSelectedCompanyParentOrganization,
    selectedCompanyId: selectedCompany?.id,
    technologyCompanyId: technology?.company?.id,
  });
  const canEdit = isInnovator && Boolean(assessmentId) && !isChildOrganizationView;

  const { isPending: isSaving, mutateAsync: saveSimilarTechnologies } = useSaveSimilarTechnologies();

  const hasMatches = Boolean(similarTechnologies && similarTechnologies.matches.length > 0);
  const isUpdateMode = hasMatches;

  const overallConfidence =
    similarTechnologies && hasMatches ? getOverallSimilarTechnologiesConfidence(similarTechnologies) : null;

  const initialSuggestionData = useMemo<Record<string, unknown> | undefined>(
    () => (isUpdateMode && similarTechnologies ? { ...similarTechnologies } : undefined),
    [isUpdateMode, similarTechnologies],
  );

  const handleAcceptData = async (value: Record<string, unknown>, meta?: { logId?: string }): Promise<boolean> => {
    if (!assessmentId) return false;

    const parsed = parseSimilarTechnologiesSummaryData(value);

    if (!parsed) {
      errorToast({ description: 'The accepted draft is missing required fields.' });
      return false;
    }

    try {
      // Accepting a chat draft marks the field AI-assisted (ADR-0007).
      await saveSimilarTechnologies({
        assessmentId,
        provenance: { llmLogId: meta?.logId },
        similarTechnologiesSummary: parsed,
        technologyId,
      });
    } catch {
      // The API hook already toasts persist failures; keeping the modal open
      // preserves the reviewed draft.
      return false;
    }

    successToast({ description: 'Updated Similar Technologies' });
    return true;
  };

  const renderDataArtifact = (value: Record<string, unknown>) => {
    const parsed = parseSimilarTechnologiesSummaryData(value);
    if (!parsed) return null;

    return <SimilarTechnologiesSuggestionContent summary={parsed} />;
  };

  const isTileLoading = ((isLoading ?? false) && !hasMatches) || isTechnologyPending;

  return (
    <>
      <Tile
        emptyState={{
          title: 'No Similar Technologies',
          action:
            canEdit &&
            (isLatestAssessment ? (
              <Button
                className="mx-auto w-fit"
                onClick={() => setIsChatOpen(true)}
                variant="link"
              >
                Find Similar Technologies
                <SparklesIcon className="size-4" />
              </Button>
            ) : (
              <StaleAssessmentTooltip className="mx-auto block w-fit">
                <Button
                  disabled
                  variant="link"
                >
                  Find Similar Technologies
                  <SparklesIcon className="size-4" />
                </Button>
              </StaleAssessmentTooltip>
            )),
        }}
        hoverReveal
        isEmpty={!hasMatches}
        isLoading={isTileLoading}
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ConfidenceScore
                design="box"
                score={overallConfidence}
                size="small"
              />
              <div className="flex flex-col leading-tight">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-800">Similar Technologies</span>
                  <FieldProvenanceBadge provenance={provenance} />
                </div>
                <span className="text-xs text-gray-500">Confidence Score</span>
              </div>
            </div>
            {canEdit && (
              <div className="flex items-center gap-2">
                {isLatestAssessment ? (
                  <IconButton.HoverReveal
                    ariaLabel="Update similar technologies"
                    forceVisible={isSaving}
                    icon={isSaving ? <Loader2Icon className="animate-spin" /> : <SparklesIcon />}
                    onClick={() => setIsChatOpen(true)}
                    variant="secondary"
                  />
                ) : (
                  <StaleAssessmentTooltip>
                    <IconButton.HoverReveal
                      ariaLabel="Update similar technologies"
                      disabled
                      icon={<SparklesIcon />}
                      variant="secondary"
                    />
                  </StaleAssessmentTooltip>
                )}
              </div>
            )}
          </div>

          {similarTechnologies && hasMatches ? (
            <SimilarTechnologiesSummaryContent summary={similarTechnologies} />
          ) : (
            <p className="text-sm text-gray-500">Generate similar technologies to populate this tile.</p>
          )}
        </div>
      </Tile>

      <LlmChatModal
        apiEndpoint={getTechnologyTrlAnalysisChatEndpoint(technologyId)}
        data={{
          ...(assessmentId && { assessmentId }),
          kind: TechnologyTrlAnalysisChatKind.SIMILAR_TECHNOLOGIES,
        }}
        description={isUpdateMode ? getUpdateDescription(technology?.name) : getCreateDescription(technology?.name)}
        generateDraftLabel={isUpdateMode ? 'Regenerate' : 'Find Similar Technologies'}
        generateDraftMessage={
          isUpdateMode
            ? 'Regenerate the similar technologies list from scratch.'
            : 'Find similar technologies for this technology.'
        }
        information={SIMILAR_TECHNOLOGIES_PROMPT_GUIDELINES}
        initialSuggestionData={initialSuggestionData}
        isOpen={isChatOpen}
        onAcceptData={handleAcceptData}
        onClose={() => setIsChatOpen(false)}
        renderDataArtifact={renderDataArtifact}
        title="Similar Technologies"
      />
    </>
  );
};
